import React, { useEffect, useState } from 'react'
import { Button, Modal, Select, DatePicker, TimePicker, Input } from 'antd';
import "./ChargerModel.less"
import moment from "moment";
import {
    useAssignChargerMutation,
    useUnassignChargerMutation,
    chargersApi,
    useGetChargerUserQuery,
} from "services/chargersService";
import Notification from "common/components/NotificationMessage/Notification";
import { useDispatch, useSelector } from "react-redux";
import { rootReducerType } from "redux/combineReducers";
import { decodeJWT } from "common/functions";
import i18n from "../../../../internationalization/i18n";
import {capitalizeFirstLetter} from "common/utils";

type Props = {
    open: boolean,
    handleOk: any,
    handleCancel: any,
    handleChange1: any,
    ChargerId: string,
    locationIdStationId: string
};

const ChargerModel: React.FC<Props> = ({ open, handleOk, handleCancel, handleChange1, ChargerId, locationIdStationId }): React.ReactElement => {
    const [roomNumber, setRoomNumber] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [ParkingSpot, setParkingSpot] = useState<string>('');
    const [term, setTerm] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [licensePlate, setLicensePlate] = useState<string>('');
    const [momentTime, setMomentTime] = useState<any>('');

    const [assignCharger, { isSuccess: assignSuccess, data: assignData }] = useAssignChargerMutation();
    const [unassignCharger, { isSuccess: unassignSuccess, data: unassignData }] = useUnassignChargerMutation();

    const { data, refetch: refetchChargerUser } = useGetChargerUserQuery({
		locationIdStationId,
		chargerId: encodeURIComponent(ChargerId)
	});
    
    useEffect(() => {
        if(data) {
            data.contactNum && setPhoneNumber(data.contactNum)
            data.name && setName(data.name)
            data.room && setRoomNumber(data.room)
            data.parkingNum && setParkingSpot(data.parkingNum)
            data.licensePlate && setLicensePlate(data.licensePlate)
            data.email && setEmailAddress(data.email)
            if(data.expireAt) {
                const dateObject = new Date(data.expireAt);
                setMomentTime(moment(dateObject))
                setTerm(data.expireAt)
            }else{
                isDefaultTime();
            }
        }else{
            isDefaultTime();
        }
    }, [])

    const { isLoggedIn } = useSelector((state: rootReducerType) => state.authenticationReducer);

    useEffect(() => {
        refetchChargerUser()
    }, [open])

    useEffect(() => {
        if (assignSuccess) {
            Notification({
                type: assignData?.success ? "success" : "warning",
                message: i18n.t(assignData?.success ? "charger.successModalTitle" : "charger.warningModalTitle"),
                description: assignData?.success ? i18n.t("charger.successModalDesc", { action: "assign" }) :capitalizeFirstLetter(assignData?.message || "")
            });
            handleCancel();
        }
            
    }, [assignSuccess]);

    useEffect(() => {
        if (unassignSuccess) {
            Notification({
                type: unassignData?.success ? "success" : "warning",
                message: i18n.t(unassignData?.success ? unassignData.message : "charger.warningModalTitle"),
                description: unassignData?.success ? i18n.t("charger.successModalDesc", { action: "unassign" }) :capitalizeFirstLetter(unassignData?.message || ""),
            });
            handleCancel();
        }
            
    }, [unassignSuccess])

    const onChange = (dateString) => {
        // 'YYYY-MM-DDTHH:mm:ss'
        const newStartDate = moment(dateString).format('YYYY-MM-DDTHH:mm:ss').toString();
        setTerm(newStartDate);
    }

    const currentDate = moment();

    // 自定义日期不可用的函数
    const disabledDate = (current) => {
        // 只允许选择当前日期之后的日期
        return current && current <= currentDate.startOf('minute');
    };

    const isDefaultTime = () => {
        // 获取当前时间
        const currentTime = new Date();

        // 获取今天的日期
        const today = new Date();
        today.setHours(12, 0, 0, 0); // 设置为今天的12点

        // 获取明天的日期
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1); // 设置为明天的日期

        // 判断当前时间是否已经过了今天的12点
        if (currentTime >= today) {
            // 显示明天的12点时间
            setMomentTime(moment(tomorrow));
            setTerm(moment(tomorrow).format('YYYY-MM-DDTHH:mm:ss').toString());

        } else {
            // 显示今天的12点时间
            setMomentTime(moment(today));
            setTerm(moment(tomorrow).format('YYYY-MM-DDTHH:mm:ss').toString());
        }
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const saveTap = () => {
        if (!emailAddress) {
            Notification({
                type: "warning",
                message: "Email address is required",
                description: "",
            });
            return
        }
        if (!emailPattern.test(emailAddress)) {
            Notification({
                type: "warning",
                message: "Invalid email address",
                description: "",
            });
            return
        }
        assignCharger({
            locationIdStationId,
            chargerId: ChargerId,
            email: emailAddress,
            expireAt: term,
            contactNum: phoneNumber,
            licensePlate: licensePlate,
            parkingNum: ParkingSpot,
            name: name,
            room: roomNumber
        });
    }

    const clearTap = () => {

        unassignCharger({
            locationIdStationId,
            chargerId: ChargerId,
            userName: decodeJWT(isLoggedIn).email,
        });
    }

    return (
        <Modal
            visible={open}
            title="Charger - Assign to a User"
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[]}
            width={600}
        >
            <div className='model-content'>
                <div className='model-content-item'>
                    <p>Room number: </p>
                    <Input style={{ width: 320 }} placeholder="Room number"
                        value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)} />
                </div>
                <div className='model-content-item'>
                    <p>Name: </p>
                    <Input style={{ width: 320 }} placeholder="Name"
                        value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className='model-content-item'>
                    <p>Parking spot: </p>
                    <Input style={{ width: 320 }} placeholder="Parking spot"
                        value={ParkingSpot} onChange={(e) => setParkingSpot(e.target.value)} />
                </div>
                <div className='model-content-item'>
                    <p>Term:</p>
                    <DatePicker
                        disabledDate={disabledDate}
                        style={{ width: 320 }}
                        showTime // 启用时间选择
                        format="YYYY-MM-DD HH:00" // 设置日期时间格式
                        placeholder="Select Date and Time"
                        onChange={(value, dateString) => {
                            if (value) {
                                onChange(dateString);
                            } else {
                                setTerm(''); // Clear term or set to a default/initial value
                            }
                        }}
                        defaultValue={momentTime}
                    />
                </div>
                <div className='model-content-item'>
                    <p>Phone number: </p>
                    <Input style={{ width: 320 }} placeholder="Phone number"
                        value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                </div>
                <div className='model-content-item'>  
                    <p>Email address<span style={{ color: 'red' }}>*</span>:</p>
                    <Input style={{ width: 320 }} placeholder="Email address"
                        value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} required/>
                </div>
                <div className='model-content-item'>
                    <p>License plate#: </p>
                    <Input style={{ width: 320 }} placeholder="License plate#"
                        value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} />
                </div>
                {/* <div className='model-last-btn'>
                    <Button type="primary" size='large'
                        style={{ margin: "10px 0", background: '#17CA9D', border: '1px solid #17CA9D', height: '50px' }}>Set to plug and charge</Button>
                    <Button type="primary" size='large'
                        style={{ margin: "10px 0", height: '50px', background: '#568DDC' }}>Generate to access code</Button>
                    <Button type="primary" danger size='large'
                        style={{ margin: "10px 0", height: '50px', width: '82%', background: '#FD6B7E' }}>clear user info</Button>
                </div> */}
                <div className='model-last-btn'>
                    <Button type="primary" danger size='large'
                        style={{ margin: "10px 0", height: '50px', width: '48%', background: '#FD6B7E' }}
                        onClick={() => clearTap()}>clear user info</Button>
                    <Button type="primary" danger size='large'
                        style={{
                            background: '#FF9F39', border: '1px solid #FF9F39', height: '50px',
                            width: '48%', fontSize: '20px', fontWeight: '800'
                        }} onClick={() => saveTap()}>save</Button>
                </div>
            </div>
        </Modal>
    )
}


export default ChargerModel